import Vue from "vue";
import { BFormInput } from "bootstrap-vue";
import { ConnexionPageState } from "../ConnexionPageState";
import { ConnexionPageResetPassword } from "./ConnexionPageResetPassword";
import { ConnexionPageAbout } from "./ConnexionPageAbout";
import { ConnexionPageAuthBasic } from "./ConnexionPageAuthBasic";
import { ConnexionPageAuthSso } from "./ConnexionPageAuthSso";
import AuthentificationMethodEnum from "@/enums/AuthentificationMethodEnum";

/** Etat initial */
export class ConnexionPageInitial extends ConnexionPageState{
    
    /** 
     * Effectue une demande au serveur pour authentifier l'utilisateur
     * Si l'utilisateur n'est pas authentifié alors un message d'erreur est affiché
     * Si l'utilisateur est authentifié :
     *  - Si son mot de passe est conforme alors, il suit la porcédure normal
     *  - Si son mot de passe n'est pas conforme alors, Nous affichons un message d'avertissement "WeakPassword"  
     */
    public async SignIn(): Promise<void> {
        /** Début de l'overlay de chargement pour l'utilisateur */
        this.context.Vue.$store.dispatch("ShowLoadingMask", this.context.Vue.$t('LoginInProgress'));

        try {
            /** Analyse les champs avec les valeurs de l'URL */
            if( !this.context.HasLoaded && Object.keys(this.context.UrlQueryParameters).length > 0) {
                /** Si login en URL */
                if(this.context.UrlQueryParameters.Login !== undefined)
                    this.context.Login = this.context.UrlQueryParameters.Login.toString();
                /** Si mot de passe en URL */
                if(this.context.UrlQueryParameters.Password !== undefined)
                    this.context.Password = this.context.UrlQueryParameters.Password.toString();

                /** Si les parametres ne contiennent pas "Login" ou "Password" c'est considéré comme un retour du serveur SSO*/
                if( this.context.UrlQueryParameters.Login === undefined 
                    && this.context.UrlQueryParameters.Password === undefined) {
                    await this.setSso();
                    return;
                }
            }

            /** Je nettoye les stores avant de le réinitialisé afin de changer de login courant */
            await this.context.Vue.$store.dispatch('AuthentificationStore/ResetStorage');
            await this.context.Vue.$store.dispatch('AuthentificationStore/Reset');
            await this.context.Vue.$store.dispatch('OidcStore/ResetSession');

            /** 
             * Effectue un pré-traitement sur le login pour déterminer s'il existe et quelle méthode d'authentification est nécessaire. 
             * Récupère la méthode de connexion du login {Basic|OIDC} 
             */
            await this.getAuthMethod(this.context.Login);
            
        } finally {
            this.context.Vue.$store.dispatch("HideLoadingMask", {});
        }
    }

    /** Affiche le formulaire de saisie du login pour initier un reset du mot de passe */
    public ResetPassword(): void {
        /** Pré-rempli le champ avec la saisie en cours du login */
        if(this.context.Login)
            this.context.LoginForgotPassword = this.context.Login;

        /** Masque le formulaire de saisie du login */
        this.context.ShowLoginForm = false;
        /** Affiche le formulaire */
        this.context.ShowResetPasswordForm = true;
        /** Change le comportement de la vue */
        this.context.State = new ConnexionPageResetPassword(this.context);
    }

    /** Ouvre le volet d'information sur l'application DCx */
    public About(): void {
        this.context.ShowAboutInformations = true;
        this.context.State = new ConnexionPageAbout(this.context);
    }

    /** Récupère les informations d'authentification d'un login (isExist, Auth{basic, oidc}, oidcConfig) */
    private async getAuthMethod(login: string): Promise<void> {
        
        await this.context.Vue.$store.dispatch("AuthentificationStore/LoadFromLogin", login)
            .catch(err => {
                console.error(err);
                this.context.Vue.$bvToast.toast(this.context.Vue.$t('AnErrorOccured').toString(), { 
                        autoHideDelay: 10000,
                        variant: "danger",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                });
            });

        const method = await this.context.Vue.$store.getters['AuthentificationStore/GetConnectionMethod'];

        this.context.ShowLoginForm = false;

        /** actualise l'algorithme en fonction du login */
        if( method && method.method === AuthentificationMethodEnum.Oidc && method.oidcAuthority )
            await this.setSso();
        else 
            await this.setBasicAuth();
        
    }

    /** Transitionne de l'état initial vers une authentification SSO OIDC */
    private async setSso(){
        this.context.State = new ConnexionPageAuthSso(this.context);
        this.context.ShowSsoOidcInfo = true;
        await this.context.State.SignIn();
    }

    /** Transitionne de l'état initial vers une authentification basique par mot de passe*/
    private async setBasicAuth(){
        /** Cas spécial si l'URL contient uniquement le login, il faut annuler la procédure de login initié au "created" */
        if(!this.context.HasLoaded && Object.keys(this.context.UrlQueryParameters).length > 0 && this.context.UrlQueryParameters.Login !== undefined && this.context.UrlQueryParameters.Password === undefined){
            this.context.ShowLoginForm = true;
            return;
        }
        
        /** Nous sommes face à une authentification classique via mot de passe */
        this.context.State = new ConnexionPageAuthBasic(this.context);

        /** Initialisation du AuthLogin préventivement */
        this.context.AuthLogin = this.context.Login;

        /** Cas spécial si l'URL contient le login et le mot de passe, il faut automatiquement executer SignIn de la machine à état */
        if(!this.context.HasLoaded && Object.keys(this.context.UrlQueryParameters).length > 0 && this.context.UrlQueryParameters.Login !== undefined && this.context.UrlQueryParameters.Password !== undefined){
            this.context.State.SignIn();
            return;
        }

        this.context.PasswordLocked = true;
        this.context.ShowPasswordForm = true;

        /** Attend le "render" du "DOM" avant de demander le focus */
        await Vue.nextTick();
        /** Prend le focus sur le champ de saisie "mot de passe"*/
        const inputPassword = this.context.Vue.$refs.passwordRef as BFormInput;
        let inputEl: HTMLInputElement = inputPassword.$el as HTMLInputElement;
        for(let i: number = 0; i < 5 ; i++)
        {
            if(inputEl)
            {
                (inputPassword.$el as HTMLInputElement).focus();
                break;
            }
            /** Si le temps prend plus de temps que prévu pour se générer on patiente le tick suivant 5 fois max, sinon on abandonne le focus */
            await Vue.nextTick();
            inputEl = inputPassword.$el as HTMLInputElement;
        }
    }

    //#region CAS INGORE
    /** VOID */
    public async SendMail(): Promise<void> { /** Rien à envoyer */ }

    /** VOID */
    public async Abord(): Promise<void> { /** Rien à annuler */ }
    //#endregion CAS INGORE
}